import { isEqual } from "lodash";
import moment from "moment";

import { Key } from "../../constants/keyboard";
import { ADMIT_DX_ID, CodeType, DomainType, GridCodeType } from "../../models";
import { PresentOnAdmission } from "../../models/codeGrid";
import { EncounterCategory } from "../../models/encounterServiceEntity";
import { isCodeGridColumnHidden } from "../../scenes/Encounter/components/CodeGrid/Columns/codeGridColumnVisibility";
import { getCurrentIPGrouperTypeForResult } from "../../selectors/grouping";
import {
  MAX_PROVIDER_LENGTH,
  checkDDLFieldLength,
} from "../../services/encounter/encounterMapping";
import { isInvalidFormattedDate } from "../../utils/date";
import {
  combineEncounterCodes,
  isDiagnosesAnalysisActive,
  needResequenceEncounterCodes,
  resequenceCodes,
} from "../../utils/encounter";
import { getColumnFieldSetting, isHiddenField, isReadOnlyField } from "../../utils/fieldSettings";
import { checkProFeeAllowed } from "../../utils/proFeeUtils";
import { isDateValidator } from "../../validators/simpleValidators";

import { getId, updateCodes } from "./encounterCommon";

import type {
  CodeBooksModalCancelled,
  CodeBooksModalCodePosted,
} from "../../components/TEEModals/codeBooksModalActions";
import type { EncounterEntity, EncounterState } from "../../models";
import type { BaseCodeRow, CodeGroup } from "../../models/codeGrid";
import type { CodeGridsColumns } from "../../models/columns";
import type { ElixhauserAnalyzeResult } from "../../models/elixhauser";
import type {
  FacilityPreferences,
  FacilitySettingsState,
  FieldSetting,
  FieldSettings,
} from "../../models/facilitySettings";
import type { PDxAnalyzeResultEntity } from "../../models/groupingEntity";
import type { IdDescriptionBase } from "../../models/patientEncounter";
import type { UserFeaturesState } from "../../models/user";
import type {
  CodeGridChange,
  CodeGridCodeBlur,
  CodeGridDragDropRow,
  CodeGridExitEditInfo,
  CodeGridKeypress,
} from "../../scenes/Encounter/actions/codeGrid";
import type { EditLocateEvent } from "../../scenes/Encounter/actions/researchPane";
import type { CodeGridColumnVisibilityOptions } from "../../scenes/Encounter/components/CodeGrid/Columns/codeGridColumnVisibility";

const isAnalysisNavLocked = (payload?: {
  isShowingPdxAnalysis?: boolean;
  isShowingEccAnalysis?: boolean;
}): boolean =>
  isDiagnosesAnalysisActive(payload?.isShowingPdxAnalysis, payload?.isShowingEccAnalysis);

export const handleCodeGridChange = (
  state: EncounterState,
  payload: CodeGridChange,
): EncounterState => {
  const changedTime = moment.now();
  // no need to update admit diagnosis code here
  if (payload.dataItem.id === ADMIT_DX_ID) {
    return state;
  }

  // update currently edited row. It will not be sent to the services until blur
  const item = findItem(state.inProgress, payload.dataItem);
  if (!item) {
    return state;
  }

  let { dirty } = state;
  const inProgress = {
    ...state.inProgress,
  };
  const newItem = {
    ...item,
    autoChanged: [...(item.autoChanged ?? [])],
    inEdit: item.inEdit,
    editField: item.editField,
  };

  if (typeof payload.field !== "undefined" && typeof payload.value !== "undefined") {
    // For DxLinks, we need to compare the entire array
    if (payload.field === "DxLinks") {
      const oldDxLinks = item.DxLinks || [];
      const newDxLinks = payload.value as unknown as string[];
      if (!dirty && !isEqual(oldDxLinks, newDxLinks)) {
        dirty = true;
      }
    } else if (!dirty && newItem[payload.field] !== payload.value) {
      dirty = true;
    }

    // autofill using Episode
    if (payload.field === "episode" && payload.value && newItem[payload.field] !== payload.value) {
      const fillFromCode = findEpisodeCode(
        inProgress,
        newItem.gridCodeType,
        payload.value,
        newItem.id,
      );
      if (fillFromCode) {
        if (newItem.date !== fillFromCode.date) {
          newItem.date = fillFromCode.date;
          newItem.autoChanged.push("date");
        }
        if (newItem.provider !== fillFromCode.provider) {
          newItem.provider = fillFromCode.provider;
          newItem.autoChanged.push("provider");
        }
      }
    }

    newItem[payload.field] = payload.value;
    if (payload.field === "description") {
      // description is generated field. duplicate it in the user field
      newItem.customCodeDescription = payload.value;
    }
    newItem.valid = undefined;
    if (!(payload.field === "code" && payload.value.trim() === "")) {
      newItem.empty = false;
    }
    // Keep the live editField from Redux. A deferred editor save (e.g. Modifiers blur)
    // must not pull focus back after the user already entered another cell.
    newItem.editField = item.editField ?? payload.field;
  }

  updateItem(inProgress, payload.dataItem.gridCodeType, payload.dataItem.id, newItem);
  updateCodes(inProgress);

  return {
    ...state,
    dirty,
    inProgress,
    lastChangeTime: changedTime,
  };
};

export const handleCodeGridAutoChange = (
  state: EncounterState,
  payload: CodeGridChange,
): EncounterState => {
  // update currently edited row. It will not be sent to the services until blur
  const item = findItem(state.inProgress, payload.dataItem);
  if (!item?.autoChanged?.length) {
    return state;
  }

  const { dirty } = state;
  const inProgress = {
    ...state.inProgress,
  };
  const newItem = {
    ...item,
    // remove all autoChanged.
    // if this expands to more fields than provider/date then more complicated logic will need to be written
    autoChanged: [] as string[],
  };

  updateItem(inProgress, payload.dataItem.gridCodeType, payload.dataItem.id, newItem);
  updateCodes(inProgress);

  return {
    ...state,
    dirty,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleCodeBooksModalCodePostedAdmitDX = (
  state: EncounterState,
  payload: CodeBooksModalCodePosted,
): EncounterState => {
  if (!payload.codes || payload.codes.length === 0) {
    return state;
  }

  const isSameCode = state.inProgress.admitDiagnosisCode.initialCode === payload.codes[0].code;

  return {
    ...state,
    dirty: true,
    inProgress: {
      ...state.inProgress,
      admitDiagnosisCode: {
        ...state.inProgress.admitDiagnosisCode,
        code: payload.codes[0].code,
        description: isSameCode ? state.inProgress.admitDiagnosisCode.description : undefined,
        customCodeDescription:
          isSameCode ? state.inProgress.admitDiagnosisCode.customCodeDescription : undefined,
        ServiceCodeDescription:
          isSameCode ? state.inProgress.admitDiagnosisCode.ServiceCodeDescription : undefined,
        gridCodeType: GridCodeType.ADMIT_DX,
        editField: "code",
        inEdit: true,
        initialCode: payload.codes[0].code,
        serviceCode: payload.codes[0].code,
        type: payload.codes[0].codeType,
        loaded: isSameCode ? state.inProgress.admitDiagnosisCode.loaded : undefined,
      },
      lastActiveCodeId: state.inProgress.admitDiagnosisCode.id,
    },
    lastChangeTime: moment.now(),
  };
};

export const handleCodeBooksModalCodePosted = (
  state: EncounterState,
  payload: CodeBooksModalCodePosted,
): EncounterState => {

  // LOGIC can ignore posted code if we have validation errors and keep focus on the wrong field

  // update admit diagnosis code
  if (payload.dataItem?.id === ADMIT_DX_ID) {
    return handleCodeBooksModalCodePostedAdmitDX(state, payload);
  }

  const postedCodesLength = payload.codes.length;

  if (postedCodesLength === 0) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
  };
  exitEditAll(inProgress);

  // for each code from the same codegroup with edited dataItem - insert them starting from this position
  // for each code for other grids - add them to the end
  // inEdit will be set for the first added code only if we have active dataItem
  let inEdit = payload.dataItem.gridCodeType ? true : undefined;
  let lastActiveCode = inProgress.lastActiveCodeId;
  let mainGroup: GridCodeType | null = null;
  const firstCode = payload.codes[0];
  const addedCodesIndexes = {};

  // we need to insert all payload.codes of the same type from the currently edited item position if possible
  if (canBePlacedToGrid(firstCode.codeType, payload.dataItem.gridCodeType)) {
    const codeGroup = inProgress[payload.dataItem.gridCodeType];
    const collection = codeGroup.codes;
    const newCollection: BaseCodeRow[] = [];
    const codesLen = collection.length;

    for (let pos = 0; pos < codesLen; pos++) {
      if (collection[pos].id === payload.dataItem.id) {
        const currentLine = collection[pos];
        lastActiveCode = currentLine.id;

        const isSameCode = currentLine.code === firstCode.code;
        const currentLineModifiers: string[] = currentLine.modifiers ?? [];
        const mergedFirstCodeModifiers =
          isSameCode ?
            [...new Set([...currentLineModifiers, ...(firstCode.modifiers || [])])]
          : [...(firstCode.modifiers || [])];

        const newItem = {
          ...payload.dataItem,
          code: firstCode.code,
          editField: payload.field,
          initialCode: firstCode.code,
          empty: false,
          inEdit: true,
          serviceCode: firstCode.code,
          type: firstCode.codeType,
          customCodeDescription: isSameCode ? currentLine.customCodeDescription : undefined,
          ServiceCodeDescription: isSameCode ? currentLine.ServiceCodeDescription : undefined,
          modifiers: mergedFirstCodeModifiers,
          DxLinks: [],
          units: isSameCode ? currentLine.units : undefined,
          date: autofillProcedureDate(inProgress, payload.dataItem.gridCodeType, currentLine),
          presentOnAdmission:
            isSameCode ? currentLine.presentOnAdmission : PresentOnAdmission.EMPTY,
          // do not replace filled provider and revenue code with default for the existing codes
          provider:
            isSameCode || currentLine.provider ?
              currentLine.provider
            : autofillDefaultProvider(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.facilitySettings,
                payload.providers,
              ),
          revenueCode:
            isSameCode || currentLine.revenueCode ?
              currentLine.revenueCode
            : autofillDefaultRevenueCode(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.facilitySettings,
              ),
          placeOfService:
            isSameCode || currentLine.placeOfService ?
              currentLine.placeOfService
            : autofillDefaultPlaceOfService(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.serviceLocations,
              ),
          loaded: isSameCode ? currentLine.loaded : undefined,
        };

        addedCodesIndexes[0] = true;

        newCollection.push(newItem);
        inEdit = undefined;
        mainGroup = payload.dataItem.gridCodeType;

        // insert all other codes that can be added to this grid from this position
        for (let ind = 1; ind < postedCodesLength; ind++) {
          const code = payload.codes[ind];
          if (canBePlacedToGrid(code.codeType, payload.dataItem.gridCodeType)) {
            newCollection.push({
              code: code.code,
              empty: false,
              gridCodeType: payload.dataItem.gridCodeType,
              id: getId(),
              inEdit: undefined,
              editField: undefined,
              initialCode: code.code,
              serviceCode: code.code,
              type: code.codeType,
              date: autofillProcedureDate(inProgress, payload.dataItem.gridCodeType),
              provider: autofillDefaultProvider(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.facilitySettings,
                payload.providers,
              ),
              modifiers: code.modifiers ? [...code.modifiers] : [],
              revenueCode: autofillDefaultRevenueCode(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.facilitySettings,
              ),
              placeOfService: autofillDefaultPlaceOfService(
                inProgress,
                payload.dataItem.gridCodeType,
                payload.serviceLocations,
              ),

              Validations: [],
            });

            addedCodesIndexes[ind] = true;
          }
        }
      }

      // skip current item to add rest of this codegroup codes
      if (collection[pos].id !== payload.dataItem.id && !collection[pos].empty) {
        newCollection.push(collection[pos]);
      }
    }

    inProgress[payload.dataItem.gridCodeType] = {
      ...inProgress[payload.dataItem.gridCodeType],
      codes: newCollection,
    };
  }

  // place every other code to the end of its group
  function addCodesToCodeGroup(codesGroup: CodeGroup, gridCodeType: GridCodeType) {
    if (gridCodeType === mainGroup) {
      // already handled
      return codesGroup;
    }

    const newCodes: BaseCodeRow[] = [];
    for (let ind = 0, codesGroupLen = codesGroup.codes.length; ind < codesGroupLen; ind++) {
      if (!codesGroup.codes[ind].empty) {
        newCodes.push(codesGroup.codes[ind]);
      }
    }

    for (let ind = 0; ind < postedCodesLength; ind++) {
      const code = payload.codes[ind];
      // code added from the codebooks should be focused on (last code only)
      inEdit = ind === postedCodesLength - 1;
      if (!addedCodesIndexes[ind] && canBePlacedToGrid(code.codeType, gridCodeType)) {
        const newId = getId();
        newCodes.push({
          code: code.code,
          empty: false,
          gridCodeType,
          id: newId,
          inEdit,
          editField: inEdit ? payload.field : undefined,
          initialCode: code.code,
          serviceCode: code.code,
          type: code.codeType,
          modifiers: code.modifiers ? [...code.modifiers] : [],
          date: autofillProcedureDate(inProgress, gridCodeType),
          provider: autofillDefaultProvider(
            inProgress,
            gridCodeType,
            payload.facilitySettings,
            payload.providers,
          ),
          revenueCode: autofillDefaultRevenueCode(
            inProgress,
            gridCodeType,
            payload.facilitySettings,
          ),
          placeOfService: autofillDefaultPlaceOfService(
            inProgress,
            gridCodeType,
            payload.serviceLocations,
          ),

          Validations: [],
        });

        if (inEdit) {
          lastActiveCode = newId;
        }

        inEdit = undefined;
        addedCodesIndexes[ind] = true;
      }
    }

    return {
      ...codesGroup,
      codes: newCodes,
    };
  }

  // both diagnoses and visitReasons can hold same codes. Use gridCodeType from the dataItem if possible
  const allGridTypes = [
    GridCodeType.DIAGNOSES,
    GridCodeType.VISITREASONS,
    GridCodeType.INPROCEDURES,
    GridCodeType.OUTPROCEDURES,
    GridCodeType.PROFESSIONAL_PROCEDURES,
  ];

  // Prioritize explicit grid selection by moving it to the front
  if (!mainGroup && payload.dataItem.gridCodeType) {
    const targetGrid = payload.dataItem.gridCodeType;
    const targetIndex = allGridTypes.indexOf(targetGrid);
    if (targetIndex > -1) {
      // Move target grid to front of array
      allGridTypes.splice(targetIndex, 1);
      allGridTypes.unshift(targetGrid);
    }
  }

  for (const gridType of allGridTypes) {
    // Check if any code from payload.codes can be placed in this grid
    const canPlaceAnyCode = payload.codes.some(
      (code, index) => !addedCodesIndexes[index] && canBePlacedToGrid(code.codeType, gridType),
    );

    if (canPlaceAnyCode) {
      switch (gridType) {
        case GridCodeType.VISITREASONS:
          inProgress.visitReasons = addCodesToCodeGroup(
            inProgress.visitReasons,
            GridCodeType.VISITREASONS,
          );
          break;
        case GridCodeType.DIAGNOSES:
          inProgress.diagnoses = addCodesToCodeGroup(inProgress.diagnoses, GridCodeType.DIAGNOSES);
          break;
        case GridCodeType.INPROCEDURES:
          inProgress.inProcedures = addCodesToCodeGroup(
            inProgress.inProcedures,
            GridCodeType.INPROCEDURES,
          );
          break;
        case GridCodeType.OUTPROCEDURES:
          inProgress.outProcedures = addCodesToCodeGroup(
            inProgress.outProcedures,
            GridCodeType.OUTPROCEDURES,
          );
          break;
        case GridCodeType.PROFESSIONAL_PROCEDURES:
          inProgress.professionalProcedures = addCodesToCodeGroup(
            inProgress.professionalProcedures,
            GridCodeType.PROFESSIONAL_PROCEDURES,
          );
          break;
      }
    }
  }

  // it will create empty items for each type
  updateCodes(inProgress);
  inProgress.lastActiveCodeId = lastActiveCode;

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleCodeBooksModalCancelled = (
  state: EncounterState,
  payload: CodeBooksModalCancelled,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  // update admit diagnosis code
  if (payload.dataItem?.id === ADMIT_DX_ID) {
    return {
      ...state,
      inProgress: {
        ...state.inProgress,
        admitDiagnosisCode: {
          ...state.inProgress.admitDiagnosisCode,
          editField: "code",
          inEdit: true,
        },
        lastActiveCodeId: state.inProgress.admitDiagnosisCode.id,
      },
    };
  }

  if (!payload.dataItem) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
  };

  exitEditAll(inProgress);
  const collection = inProgress[payload.dataItem.gridCodeType].codes;

  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id === payload.dataItem.id) {
      updateItem(inProgress, payload.dataItem.gridCodeType, collection[ind].id, {
        ...collection[ind],
        inEdit: true,
      });
      inProgress.lastActiveCodeId = collection[ind].id;
      break;
    }
  }

  return {
    ...state,
    inProgress,
  };
};

export const handleCodeGridCodeBlurAdmitDX = (
  state: EncounterState,
  payload: CodeGridCodeBlur,
): EncounterState => {
  const isSameCode = state.inProgress.admitDiagnosisCode.initialCode === payload.value;
  return {
    ...state,
    dirty: isSameCode ? state.dirty : true,
    inProgress: {
      ...state.inProgress,
      admitDiagnosisCode: {
        ...state.inProgress.admitDiagnosisCode,
        ...payload.dataItem,
        code: payload.value,
        inEdit: undefined,
        editField: undefined,
        initialCode: payload.value,
        customCodeDescription:
          isSameCode ? state.inProgress.admitDiagnosisCode.customCodeDescription : undefined,
        description: isSameCode ? state.inProgress.admitDiagnosisCode.description : undefined,
        ServiceCodeDescription:
          isSameCode ? state.inProgress.admitDiagnosisCode.ServiceCodeDescription : undefined,
        loaded: isSameCode ? state.inProgress.admitDiagnosisCode.loaded : undefined,
        type: payload.value === "" ? CodeType.ICD10CM_DX : state.inProgress.admitDiagnosisCode.type,
      },
    },
    lastChangeTime: isSameCode ? state.lastChangeTime : moment.now(),
  };
};

const isEmptyValue = (value: string | null | undefined) => {
  if (typeof value === "string") {
    return !value.trim();
  }

  return !value;
};

const isOutProceduresRowEmpty = (row: BaseCodeRow | null | undefined) => {
  if (!row) {
    return false;
  }

  return (
    row.gridCodeType === GridCodeType.OUTPROCEDURES &&
    isEmptyValue(row.code) &&
    isEmptyValue(row.revenueCode)
  );
};

const removeInvalidOutProceduresRow = (
  entity: EncounterEntity,
  row: BaseCodeRow | null | undefined,
) => {
  if (!row) {
    return entity;
  }

  const group = entity[row.gridCodeType];

  return {
    ...entity,
    [row.gridCodeType]: {
      ...group,
      codes: group?.codes.filter((code) => code.id !== row.id),
    },
  } as EncounterEntity;
};

const shouldRemoveRowAsEmpty = (row: BaseCodeRow) =>
  !row.empty &&
  (row.gridCodeType === GridCodeType.OUTPROCEDURES ?
    isOutProceduresRowEmpty(row)
  : (row.code ?? "").trim() === "");

// clear any empty row except current row and focused row
const handleClearEmptyNotFocusedRowsInGrid = (
  inProgress: EncounterEntity,
  dataItem: BaseCodeRow,
): EncounterEntity => {
  const collection = inProgress[dataItem.gridCodeType].codes;

  const newCollection: BaseCodeRow[] = [];
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    const row = collection[ind];
    const isRemoveRowAsEmpty = shouldRemoveRowAsEmpty(row);
    if (!isRemoveRowAsEmpty || row.inEdit || row.id === dataItem.id) {
      newCollection.push(row);
    }
  }

  inProgress[dataItem.gridCodeType] = {
    ...inProgress[dataItem.gridCodeType],
    codes: newCollection,
  };

  return inProgress;
};

export const handleCodeGridCodeBlur = (
  state: EncounterState,
  payload: CodeGridCodeBlur,
): EncounterState => {
  const changedTime = moment.now();
  // update admit diagnosis code
  if (payload.dataItem?.id === ADMIT_DX_ID) {
    return handleCodeGridCodeBlurAdmitDX(state, payload);
  }

  let inProgress = {
    ...state.inProgress,
  };

  inProgress = handleClearEmptyNotFocusedRowsInGrid(inProgress, payload.dataItem);

  const codeGroup = inProgress[payload.dataItem.gridCodeType];
  const collection = codeGroup.codes;

  // remove this dataItem if we clear code
  // special case for outProcedures: if other allowed fields are present, keep the row
  if (!payload.dataItem.empty && payload.value.trim() === "" && !payload.blurToCodebooks) {
    const isOutProcedures = payload.dataItem.gridCodeType === GridCodeType.OUTPROCEDURES;
    const liveItem = findItem(inProgress, payload.dataItem);
    // Stale editor props still have inEdit=true after a cell switch. Keep the row
    // only for a real revenue code, or when focus moved to another field on this row.
    const stayingOnAnotherField =
      !!liveItem?.inEdit && !!liveItem.editField && liveItem.editField !== "code";
    const hasOutProceduresAllowedValues =
      isOutProcedures && (!!liveItem?.revenueCode || stayingOnAnotherField);

    if (hasOutProceduresAllowedValues) {
      // don't remove the row; user may be filling revenueCode without code
      // we need to clear serviceCode to trigger backend update
      const item = findItem(inProgress, payload.dataItem);
      if (item?.serviceCode) {
        const clearedItem = {
          ...item,
          code: "",
          serviceCode: "",
          loaded: undefined,
          customCodeDescription: undefined,
          ServiceCodeDescription: undefined,
        };
        updateItem(inProgress, payload.dataItem.gridCodeType, payload.dataItem.id, clearedItem);
      }

      return { ...state, dirty: true, inProgress, lastChangeTime: changedTime };
    }

    const newCollection: BaseCodeRow[] = [];
    for (let ind = 0, len = collection.length; ind < len; ind++) {
      if (collection[ind].id !== payload.dataItem.id) {
        newCollection.push(collection[ind]);
      }
    }

    const newCodeGroup = { ...codeGroup, codes: newCollection };
    inProgress[payload.dataItem.gridCodeType] = newCodeGroup;
    updateCodes(inProgress);

    return {
      ...state,
      inProgress,
      lastChangeTime: changedTime,
    };
  }

  // check if code is different from the service value. Do it only after blur
  if (payload.value !== payload.dataItem.serviceCode) {
    // update currently edited row
    const item = findItem(state.inProgress, payload.dataItem);

    if (!item) {
      return state;
    }

    // autofill date only for the new entered codes
    const procedureDate =
      (!item.initialCode || !item.serviceCode) && payload.value ?
        autofillProcedureDate(inProgress, payload.dataItem.gridCodeType, item)
      : item.date;
    const provider =
      (!item.initialCode || !item.serviceCode) && payload.value && !item.provider ?
        autofillDefaultProvider(
          inProgress,
          payload.dataItem.gridCodeType,
          payload.facilitySettings,
          payload.providers,
        )
      : item.provider;
    const revenueCode =
      (!item.initialCode || !item.serviceCode) && payload.value && !item.revenueCode ?
        autofillDefaultRevenueCode(
          inProgress,
          payload.dataItem.gridCodeType,
          payload.facilitySettings,
        )
      : item.revenueCode;
    const placeOfService =
      (!item.initialCode || !item.serviceCode) && payload.value.trim() && !item.placeOfService ?
        autofillDefaultPlaceOfService(
          inProgress,
          payload.dataItem.gridCodeType,
          payload.serviceLocations,
        )
      : item.placeOfService;

    const prevServiceCode = (item.serviceCode ?? "").trim();
    const newCodeTrimmed = payload.value.trim();
    const isCptHcpcsGrid =
      payload.dataItem.gridCodeType === GridCodeType.OUTPROCEDURES ||
      payload.dataItem.gridCodeType === GridCodeType.PROFESSIONAL_PROCEDURES;
    // Replace one committed CPT/HCPCS code with another: clear ancillary fields
    // If there was no committed code yet (blank row / first entry), keep modifiers & units
    const isReplacingCommittedProcedureCode =
      isCptHcpcsGrid &&
      prevServiceCode !== "" &&
      newCodeTrimmed !== "" &&
      prevServiceCode.toUpperCase() !== newCodeTrimmed.toUpperCase();

    const newItem = {
      ...item,
      presentOnAdmission: PresentOnAdmission.EMPTY,
      code: payload.value,
      serviceCode: payload.value,
      empty: payload.value.trim() === "",
      loaded: undefined, // mark changed code as not synchronized with services
      customCodeDescription: undefined,
      ServiceCodeDescription: undefined,
      inEdit: item.inEdit,
      editField: item.editField,
      DxLinks: isReplacingCommittedProcedureCode ? [] : item.DxLinks,
      modifiers: isReplacingCommittedProcedureCode ? [] : item.modifiers,
      units: isReplacingCommittedProcedureCode ? undefined : item.units,
      placeOfService,
      date: procedureDate,
      provider,
      revenueCode,
    } as BaseCodeRow;

    updateItem(inProgress, payload.dataItem.gridCodeType, payload.dataItem.id, newItem);
    updateCodes(inProgress);

    return {
      ...state,
      inProgress,
      lastChangeTime: changedTime,
    };
  }

  // ignore equal values in the existing cells
  if (
    payload.value === payload.dataItem.initialCode ||
    (!payload.value && !payload.dataItem.initialCode)
  ) {
    return state;
  }

  // recheck all codes if we have any changes
  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
    lastChangeTime: changedTime,
  };
};

// blur from any cell - analyze changes here
export const handleCodeGridFieldBlur = (
  state: EncounterState,
  dataItem: BaseCodeRow,
): EncounterState => {
  const changedTime = moment.now();
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  if (!dataItem || dataItem.id === ADMIT_DX_ID) {
    return state;
  }

  const item = findItem(state.inProgress, dataItem);
  if (!item) {
    return state;
  }

  // check description changes
  const customDescription = dataItem.customCodeDescription ? dataItem.customCodeDescription : "";
  let needUpdate = false;
  if (customDescription) {
    const codeDescription = dataItem.ServiceCodeDescription;
    // description was changed
    if (
      customDescription !== codeDescription &&
      customDescription !== dataItem.serviceCustomDescription
    ) {
      needUpdate = true;
    }
  } else if (dataItem.serviceCustomDescription) {
    // description was cleared
    needUpdate = true;
  }

  if (needUpdate) {
    // update changed description on the services side
    const inProgress = {
      ...state.inProgress,
    };

    const newItem = {
      ...dataItem,
      serviceCustomDescription: customDescription,
    };

    updateItem(inProgress, dataItem.gridCodeType, dataItem.id, newItem);
    updateCodes(inProgress);

    return {
      ...state,
      inProgress,
      lastChangeTime: changedTime,
    };
  }

  return state;
};

export const handleCodeGridKeypress = (
  state: EncounterState,
  payload: CodeGridKeypress,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  // ignore navigation in pdx / ecc analysis mode
  const { isShowingPdxAnalysis, isShowingEccAnalysis } = payload;
  if (isDiagnosesAnalysisActive(isShowingPdxAnalysis, isShowingEccAnalysis)) {
    // special situation: Tab-Enter from AdmitDX in pdx analysis mode
    const shiftEnterFromAdmitDx =
      payload.field === "code" && payload.key === Key.Enter && payload.shiftKey;
    if (
      payload.dataItem.id === ADMIT_DX_ID &&
      (payload.key === Key.Tab || payload.key === Key.Enter) &&
      !shiftEnterFromAdmitDx
    ) {
      const inProgress = {
        ...state.inProgress,
      };

      // skip AdmitDX Description for empty code
      const currentAdmitDX = (inProgress.admitDiagnosisCode.code || "").trim();
      if (
        (payload.fieldSettings &&
          isHiddenField(payload.fieldSettings, "AdmitDiagnosis.Description")) ||
        currentAdmitDX === ""
      ) {
        inProgress.admitDiagnosisCode = {
          ...inProgress.admitDiagnosisCode,
          editField: "code",
          inEdit: true,
        };
        inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
      } else {
        inProgress.admitDiagnosisCode = {
          ...inProgress.admitDiagnosisCode,
          editField: "description",
          inEdit: true,
        };
        inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
      }

      updateCodes(inProgress);

      return {
        ...state,
        inProgress,
      };
    }

    return state;
  }

  // not PDX Analysis mode
  switch (payload.key) {
    case Key.Enter: {
      let inProgress = { ...state.inProgress };

      // shift-Enter - navigate one row up to the same column
      if (payload.shiftKey) {
        handlePrevRowFocus(
          state,
          inProgress,
          payload.dataItem,
          payload.field || "code",
          true,
          payload,
        );
        // control-Enter - navigate one row down to the code column
      } else if (payload.ctrlKey) {
        handleNextRowFocus(state, inProgress, payload.dataItem, "code", true, payload);
        // usual Enter - navigate one row down to the same column
      } else {
        handleNextRowFocus(
          state,
          inProgress,
          payload.dataItem,
          payload.field || "code",
          true,
          payload,
        );
      }
      const currentForEnter = findItem(inProgress, payload.dataItem);
      // Prefer typed code from keypress: CODE_GRID_BLUR may still be racing (legacy TRY_BLUR).
      const typedCode = (payload.dataItem?.code ?? currentForEnter?.code ?? "").trim();
      const wasEmptyOut = isOutProceduresRowEmpty(currentForEnter) && typedCode === "";
      if (wasEmptyOut) {
        inProgress = removeInvalidOutProceduresRow(inProgress, currentForEnter);
      }

      updateCodes(inProgress);

      return {
        ...state,
        inProgress,
      };
    }

    // shif + Tab - previous field
    case Key.Tab: {
      let inProgress = { ...state.inProgress };

      const columns = getGridColumns(
        state,
        payload.dataItem.gridCodeType,
        isShowingPdxAnalysis,
        payload.columnsSettings,
        payload.fieldSettings,
        isShowingEccAnalysis,
      );

      // back direction
      if (payload.shiftKey) {
        const previousField = getPreviousField(
          state,
          inProgress,
          payload.dataItem,
          columns,
          payload.field || "",
        );
        if (previousField) {
          handleCurrentRowFocus(
            inProgress,
            payload.dataItem,
            previousField,
            (payload.dataItem?.code ?? "").trim(),
          );
        } else {
          handlePrevRowFocus(state, inProgress, payload.dataItem, "", false, payload, columns);
        }

        updateCodes(inProgress);

        return {
          ...state,
          inProgress,
        };
      }

      // forward direction
      if (payload.dataItem.id === ADMIT_DX_ID) {
        // skip AdmitDX Description for empty code
        const currentAdmitDX = (inProgress.admitDiagnosisCode.code || "").trim();
        if (
          (payload.fieldSettings &&
            isHiddenField(payload.fieldSettings, "AdmitDiagnosis.Description")) ||
          currentAdmitDX === ""
        ) {
          // focus next grid if description is hidden
          const nextGrid = nextGridType(
            GridCodeType.ADMIT_DX,
            payload.fieldSettings || {},
            isDiagnosesAnalysisActive(isShowingPdxAnalysis, isShowingEccAnalysis),
            payload?.facilityPreferences,
            payload?.inpatientEncounter,
            payload?.userFeatures,
          );
          focusFirstGridRow(state, inProgress, nextGrid, payload);
        } else {
          inProgress.admitDiagnosisCode = {
            ...inProgress.admitDiagnosisCode,
            editField: "description",
            inEdit: true,
          };
          inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
        }
      } else {
        const item = findItem(inProgress, payload.dataItem);
        let nextField = getNextField(state, inProgress, item, columns, payload.field || "");
        // Skip to next row/grid only for truly empty code lines. Typed value may still be
        // only on the keypress payload while CODE_GRID_BLUR_CODE_FIELD is in-flight
        // (redux-logic), so prefer payload code over store `empty`.
        const typedCode = (payload.dataItem?.code ?? item?.code ?? "").trim();
        if (
          payload.field === "code" &&
          item?.empty &&
          typedCode === "" &&
          item?.gridCodeType !== GridCodeType.OUTPROCEDURES
        ) {
          nextField = "";
        }

        // special situation: tab after code deleting - focus first cell in the row that would replace deleted one
        const justDeletedRowIndex = getJustDeletedRowIndex(payload);

        if (nextField && justDeletedRowIndex === -1) {
          handleCurrentRowFocus(inProgress, payload.dataItem, nextField, typedCode);
        } else {
          const currentForEnter = findItem(state.inProgress, payload.dataItem);

          if (isOutProceduresRowEmpty(currentForEnter)) {
            inProgress = removeInvalidOutProceduresRow(inProgress, currentForEnter);
          }

          handleNextRowFocus(
            state,
            inProgress,
            payload.dataItem,
            "",
            justDeletedRowIndex !== -1,
            payload,
            columns,
          );
        }
      }

      updateCodes(inProgress);

      return {
        ...state,
        inProgress,
      };
    }

    case Key.Escape: {
      // simple exit editing mode for not 'code' fields
      const currentForEnter = findItem(state.inProgress, payload.dataItem);
      let escapeInProgress = state.inProgress;
      if (isOutProceduresRowEmpty(currentForEnter)) {
        escapeInProgress = removeInvalidOutProceduresRow(escapeInProgress, currentForEnter);
      }

      if (payload.field !== "code") {
        const inProgress = {
          ...escapeInProgress,
          lastActiveCodeId: undefined,
        };
        exitEditAll(inProgress);

        updateCodes(inProgress);

        return {
          ...state,
          inProgress,
        };
      }

      // restore initial code for the 'code' field
      if (payload.dataItem.id === ADMIT_DX_ID) {
        return {
          ...state,
          inProgress: {
            ...escapeInProgress,
            admitDiagnosisCode: {
              ...escapeInProgress.admitDiagnosisCode,
              code: escapeInProgress.admitDiagnosisCode.initialCode,
              inEdit: undefined,
              editField: undefined,
            },
            lastActiveCodeId: undefined,
          },
          lastChangeTime: moment.now(),
        };
      }

      const inProgress = {
        ...escapeInProgress,
      };

      const item = findItem(escapeInProgress, payload.dataItem);
      if (!item) {
        updateCodes(inProgress);
        return {
          ...state,
          inProgress,
        };
      }

      exitEditAll(inProgress);
      inProgress.lastActiveCodeId = undefined;

      const newCode = item.empty ? "" : item.initialCode;

      const newItem = {
        ...item,
        inEdit: undefined,
        editField: undefined,
        code: newCode,
      };

      const hasRevenueCode =
        item.gridCodeType === GridCodeType.OUTPROCEDURES && !isEmptyValue(item.revenueCode);

      if (newCode || hasRevenueCode) {
        updateItem(inProgress, payload.dataItem.gridCodeType, payload.dataItem.id, newItem);
      } else {
        // delete row
        const newCollection: BaseCodeRow[] = [];
        const { codes } = inProgress[payload.dataItem.gridCodeType];
        for (let ind = 0, len = codes.length; ind < len; ind++) {
          if (codes[ind].id !== item.id) {
            newCollection.push(codes[ind]);
          }
        }

        inProgress[payload.dataItem.gridCodeType] = {
          ...inProgress[payload.dataItem.gridCodeType],
          codes: newCollection,
        };

        updateCodes(inProgress);

        return {
          ...state,
          inProgress,
        };
      }

      updateCodes(inProgress);

      return {
        ...state,
        inProgress,
        lastChangeTime: moment.now(),
      };
    }

    case Key.G:
      let inProgress = { ...state.inProgress };

      exitEditAll(inProgress);
      if (payload.dataItem.id === ADMIT_DX_ID) {
        if (payload.fieldSettings) {
          const nextGrid = nextGridType(
            GridCodeType.ADMIT_DX,
            payload.fieldSettings,
            isDiagnosesAnalysisActive(isShowingPdxAnalysis, isShowingEccAnalysis),
            payload?.facilityPreferences,
            payload?.inpatientEncounter,
            payload?.userFeatures,
          );
          focusFirstGridRow(state, inProgress, nextGrid, payload);
        }
      } else {
        const item = findItem(state.inProgress, payload.dataItem);
        if (!item) {
          return state;
        }
        const currentForEnter = findItem(state.inProgress, payload.dataItem);
        if (isOutProceduresRowEmpty(currentForEnter)) {
          inProgress = removeInvalidOutProceduresRow(inProgress, currentForEnter);
        }
        const nextGrid = nextGridType(
          item.gridCodeType,
          payload?.fieldSettings || {},
          isAnalysisNavLocked(payload),
          payload?.facilityPreferences,
          payload?.inpatientEncounter,
          payload?.userFeatures,
        );

        focusFirstGridRow(state, inProgress, nextGrid, payload);
      }

      return {
        ...state,
        inProgress,
      };
    default:
      break;
  }

  return state;
};

function isDxLinkEmpty(field: string, dataItem: BaseCodeRow): boolean {
  if (!field.includes("dxLink") || field === "dxLink1") {
    return false;
  }

  const match = /dxLink(\d+)/.exec(field);
  const index = match ? parseInt(match[1], 10) - 1 : 0;

  // Missing DxLinks (null/undefined) means prior slots were never filled — same as
  // DxLinkCell.isDisabled(). The old `!!DxLinks && !prev` treated null as "not empty"
  // and let Tab reach dxLink2 after setTimeout-blur was removed (TEC-7092).
  return !dataItem.DxLinks?.[index - 1];
}

function isCellLocked(
  state: EncounterState,
  inProgress: EncounterEntity,
  dataItem: BaseCodeRow,
  field,
) {
  // Now we have the only option to lock the cells: exempt POA with the InPatientAutofillPOAForExemptDiagnosisCodes preference

  // we lock Exemp POA cells if InPatientAutofillPOAForExemptDiagnosisCodes is true in inpatient mode
  if (
    inProgress.ValidationResult?.Category !== EncounterCategory.Inpatient ||
    !state.partialFacilityPreferences?.InPatientAutofillPOAForExemptDiagnosisCodes
  ) {
    return false;
  }

  if (field !== "presentOnAdmission") {
    return false;
  }

  if (dataItem?.exemptPoa !== undefined) {
    return true;
  }

  return false;
}

function getPreviousField(
  state: EncounterState,
  inProgress: EncounterEntity,
  dataItem: BaseCodeRow,
  columns: string[],
  field: string,
) {
  let ind = columns.length - 1;
  while (ind >= 0) {
    if (columns[ind] === field) {
      break;
    }
    ind--;
  }

  // last field
  if (field === "") {
    ind = columns.length;
  }

  if (ind <= 0) {
    return "";
  }

  // find first not locked cell
  do {
    const newField = columns[ind - 1];
    if (
      !isCellLocked(state, inProgress, dataItem, newField) &&
      !isDxLinkEmpty(newField, dataItem)
    ) {
      return newField;
    }

    ind--;
  } while (ind > 0);

  return "";
}

function getNextField(
  state: EncounterState,
  inProgress: EncounterEntity,
  dataItem: BaseCodeRow,
  columns: string[],
  field: string,
) {
  const len = columns.length;
  if (len === 0) {
    return "";
  }

  let ind = 0;
  while (ind < len) {
    if (columns[ind] === field) {
      break;
    }

    ind++;
  }

  // first field
  if (field === "") {
    ind = -1;
  }

  if (ind >= len - 1) {
    return "";
  }

  // find first not locked cell
  do {
    const newField = columns[ind + 1];
    if (
      !isCellLocked(state, inProgress, dataItem, newField) &&
      !isDxLinkEmpty(newField, dataItem)
    ) {
      return newField;
    }

    ind++;
  } while (ind < len - 1);

  return "";
}

function getCodeGridColumnVisibilityFromState(
  state: EncounterState,
): CodeGridColumnVisibilityOptions {
  const isInPatient = state.inProgress.ValidationResult?.Category === EncounterCategory.Inpatient;
  const ipGrouperType =
    getCurrentIPGrouperTypeForResult(
      isInPatient,
      state.inProgress,
      state.inProgress.ipGroupingType,
    ) || "";

  return {
    isPracticeMode: state.inProgress.domain === DomainType.Practice,
    isInPatient,
    isAPRDrgGrouper: ipGrouperType === "APR",
    showGrouperFlagOnOP: state.partialFacilityPreferences?.OutpatientPreferences?.CalculateDrgs,
  };
}

function getFocusableFields(
  columnsSettings: CodeGridsColumns,
  gridKey: string,
  columnsToSkip: string[],
  fieldSettings?: FieldSettings,
  columnVisibility?: CodeGridColumnVisibilityOptions,
) {
  const defaultColumns = ["code", "description"];
  if (!columnsSettings[gridKey]) {
    return defaultColumns;
  }

  const { columnsOrder } = columnsSettings[gridKey];
  const columns: string[] = [];
  for (let ind = 0, len = columnsOrder.length; ind < len; ind++) {
    const columnKey = columnsOrder[ind];
    const column = columnsSettings[gridKey].columnsInfo[columnKey];
    // no need to filter by OP grouping because all this columns are not focusable
    const fieldSetting: FieldSetting =
      fieldSettings ? getColumnFieldSetting(fieldSettings, gridKey, columnKey) : {};

    if (
      column &&
      !column.noEditHack &&
      column.focusField &&
      !fieldSetting.hidden &&
      !columnsToSkip.includes(column.focusField) &&
      !(columnVisibility && isCodeGridColumnHidden(columnVisibility, columnKey))
    ) {
      columns.push(column.focusField);
    }
  }

  return columns;
}

function getGridColumns(
  encounter: EncounterState,
  gridCodeType: GridCodeType,
  isShowingPdxAnalysis?: boolean,
  columnsSettings?: CodeGridsColumns,
  fieldSettings?: FieldSettings,
  isShowingEccAnalysis?: boolean,
) {
  const defaultColumns = ["code", "description"];
  if (!columnsSettings) {
    return defaultColumns;
  }

  const columnVisibility = getCodeGridColumnVisibilityFromState(encounter);

  // we can lock columns
  const columnsToSkip: string[] = [];
  switch (gridCodeType) {
    case GridCodeType.DIAGNOSES:
      if (isShowingPdxAnalysis) {
        return columnsSettings.diagnosesPdxOn ?
            getFocusableFields(
              columnsSettings,
              "diagnosesPdxOn",
              columnsToSkip,
              fieldSettings,
              columnVisibility,
            )
          : defaultColumns;
      }

      if (isShowingEccAnalysis) {
        return columnsSettings.diagnosesEccOn ?
            getFocusableFields(
              columnsSettings,
              "diagnosesEccOn",
              columnsToSkip,
              fieldSettings,
              columnVisibility,
            )
          : defaultColumns;
      }

      return columnsSettings.diagnosesPdxOff ?
          getFocusableFields(
            columnsSettings,
            "diagnosesPdxOff",
            columnsToSkip,
            fieldSettings,
            columnVisibility,
          )
        : defaultColumns;
    case GridCodeType.VISITREASONS:
      return columnsSettings.visitReasons ?
          getFocusableFields(
            columnsSettings,
            "visitReasons",
            columnsToSkip,
            fieldSettings,
            columnVisibility,
          )
        : defaultColumns;
    case GridCodeType.INPROCEDURES:
      return columnsSettings.inProcedures ?
          getFocusableFields(
            columnsSettings,
            "inProcedures",
            columnsToSkip,
            fieldSettings,
            columnVisibility,
          )
        : defaultColumns;
    case GridCodeType.OUTPROCEDURES:
      return columnsSettings.outProcedures ?
          getFocusableFields(
            columnsSettings,
            "outProcedures",
            columnsToSkip,
            fieldSettings,
            columnVisibility,
          )
        : defaultColumns;
    case GridCodeType.PROFESSIONAL_PROCEDURES:
      return columnsSettings.professionalProcedures ?
          getFocusableFields(
            columnsSettings,
            "professionalProcedures",
            columnsToSkip,
            fieldSettings,
            columnVisibility,
          )
        : defaultColumns;
    default:
  }

  return defaultColumns;
}

// special situation with navigation from the just deleted code
const getJustDeletedRowIndex = (payload?: CodeGridKeypress) => {
  if (
    payload?.dataIndex !== undefined &&
    payload.field === "code" &&
    (payload.dataItem.code || "").trim() === "" &&
    !payload.dataItem.empty &&
    payload.dataItem.gridCodeType !== GridCodeType.OUTPROCEDURES
  ) {
    return payload.dataIndex;
  }

  return -1;
};

// moving in the current grid or to the next grid
function handleNextRowFocus(
  state: EncounterState,
  inProgress: EncounterEntity,
  item: BaseCodeRow,
  fieldName: string,
  currentGridOnly: boolean,
  payload?: CodeGridKeypress,
  // use it if fieldName is not provided
  columns?: string[],
) {
  if (item.id === ADMIT_DX_ID) {
    const currentAdmitDX = (inProgress.admitDiagnosisCode.code || "").trim();

    exitEditAll(inProgress);

    if (
      (payload?.fieldSettings &&
        isHiddenField(payload.fieldSettings, "AdmitDiagnosis.Description")) ||
      currentAdmitDX === ""
    ) {
      // focus next grid if description is hidden or we have not admitDX code
      const nextGrid = nextGridType(
        item.gridCodeType,
        payload?.fieldSettings || {},
        isAnalysisNavLocked(payload),
        payload?.facilityPreferences,
        payload?.inpatientEncounter,
        payload?.userFeatures,
      );
      focusFirstGridRow(state, inProgress, nextGrid, payload);
    } else {
      setAdmitDxInEdit(inProgress, "description");
    }

    return;
  }

  // special situation: tab or enter after code deleting
  const justDeletedRowIndex = getJustDeletedRowIndex(payload);

  const collection = inProgress[item.gridCodeType].codes;

  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id === item.id || ind === justDeletedRowIndex) {
      exitEditAll(inProgress);

      // navigate to the next grid
      if (!currentGridOnly && ind === len - 1) {
        const nextGrid = nextGridType(
          item.gridCodeType,
          payload?.fieldSettings || {},
          isAnalysisNavLocked(payload),
          payload?.facilityPreferences,
          payload?.inpatientEncounter,
          payload?.userFeatures,
        );

        focusFirstGridRow(state, inProgress, nextGrid, payload);
        break;
      }

      // start editing for the next item in the same grid. Stay on the last item
      let focusedItemId = ind === len - 1 ? ind : ind + 1;
      // move to the 'code' in the last empty row
      let focusedFieldName =
        isOutProceduresRowEmpty(collection[focusedItemId]) || collection[focusedItemId].empty ?
          "code"
        : fieldName;

      // after deletion stay at the code on the same row as deleted
      if (justDeletedRowIndex > -1) {
        focusedItemId = justDeletedRowIndex;
        focusedFieldName = "code";
      }

      const focusedItem = inProgress[item.gridCodeType].codes[focusedItemId];
      if (!focusedItem) {
        break;
      }
      let fieldNameToFocus =
        focusedFieldName || getNextField(state, inProgress, focusedItem, columns || [], "");
      // fallback to 'code'
      if (!fieldNameToFocus || isCellLocked(state, inProgress, focusedItem, fieldNameToFocus)) {
        fieldNameToFocus = "code";
      }

      setCodeRowInEdit(inProgress, item.gridCodeType, focusedItem.id, fieldNameToFocus);
      inProgress.lastActiveCodeId = focusedItem.id;

      break;
    }
  }
}

export const handleEditLocateEvent = (state: EncounterState, payload: EditLocateEvent) => {
  const { inProgress } = state;

  if (payload.gridCodeType === GridCodeType.ADMIT_DX) {
    exitEditAll(inProgress);
    inProgress.admitDiagnosisCode = {
      ...inProgress.admitDiagnosisCode,
      editField: payload.field || "code",
      inEdit: true,
    };
    inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
    return {
      ...state,
      inProgress: {
        ...inProgress,
      },
    };
  }

  const group: CodeGroup = inProgress[payload?.gridCodeType];
  if (group) {
    const rows = group.codes;
    const row = rows.find((r) => r.id === payload.viewId);

    if (row) {
      exitEditAll(inProgress);

      const newItem = {
        ...row,
        editField: payload.field || "",
        inEdit: true,
        initialCode: row.code,
      };

      inProgress.lastActiveCodeId = newItem.id;

      updateItem(inProgress, payload.gridCodeType, row.id, newItem);
      updateCodes(inProgress);

      return {
        ...state,
        inProgress: {
          ...inProgress,
        },
      };
    }
  }

  return state;
};

// moving in the current grid or to the previous grid
function handlePrevRowFocus(
  state: EncounterState,
  inProgress: EncounterEntity,
  item: BaseCodeRow,
  fieldName: string,
  currentGridOnly: boolean,
  payload?: CodeGridKeypress,
  // use it if fieldName is not provided
  columns?: string[],
) {
  const analysisActive = isAnalysisNavLocked(payload);
  if (item.id === ADMIT_DX_ID) {
    if (!currentGridOnly && !analysisActive) {
      let prevGrid =
        (
          payload &&
          payload.facilityPreferences?.InpatientPreferences?.AllowHcpcsProcedures &&
          payload.inpatientEncounter
        ) ?
          GridCodeType.OUTPROCEDURES
        : GridCodeType.INPROCEDURES;

      const isProFeeAllowed = checkProFeeAllowed(
        payload?.userFeatures?.packages ?? null,
        payload?.facilityPreferences?.GeneralPreferences?.AllowProfessionalCoding,
        payload?.facilityPreferences?.AllowProfessionalCodingForCurrentUser,
      );

      if (isProFeeAllowed) {
        prevGrid = GridCodeType.PROFESSIONAL_PROCEDURES;
      }

      focusLastGridRow(state, inProgress, prevGrid, payload);
    } else {
      inProgress.admitDiagnosisCode = {
        ...inProgress.admitDiagnosisCode,
        editField: "code",
        inEdit: true,
      };
      inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
    }

    return;
  }

  const collection = inProgress[item.gridCodeType].codes;

  // special situation: shift-tab or shift-enter after code deleting
  const justDeletedRowIndex = getJustDeletedRowIndex(payload);

  // stay on the first item
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id === item.id || ind === justDeletedRowIndex) {
      exitEditAll(inProgress);

      // navigate to the previous grid
      if (!currentGridOnly && ind === 0) {
        const prevGrid = prevGridType(
          item.gridCodeType,
          analysisActive,
          payload?.fieldSettings,
          payload?.facilityPreferences,
          payload?.inpatientEncounter,
        );

        focusLastGridRow(state, inProgress, prevGrid, payload);
        break;
      }

      // start editing for the previous item in the same grid. Stay on the first item
      const focusedItemId = ind === 0 ? ind : ind - 1;

      const focusedItem = inProgress[item.gridCodeType].codes[focusedItemId];
      if (!focusedItem) {
        break;
      }
      let fieldNameToFocus =
        fieldName || getPreviousField(state, inProgress, focusedItem, columns || [], "");
      // fallback to 'code'
      if (!fieldNameToFocus || isCellLocked(state, inProgress, focusedItem, fieldNameToFocus)) {
        fieldNameToFocus = "code";
      }
      const editField =
        isOutProceduresRowEmpty(focusedItem) || focusedItem.empty ? "code" : fieldNameToFocus;
      setCodeRowInEdit(inProgress, item.gridCodeType, focusedItem.id, editField);
      inProgress.lastActiveCodeId = focusedItem.id;

      break;
    }
  }
}

// moving in the current grid
function handleCurrentRowFocus(
  inProgress: EncounterEntity,
  item: BaseCodeRow,
  fieldName: string,
  typedCode?: string,
) {
  if (item.id === ADMIT_DX_ID) {
    return;
  }

  const collection = inProgress[item.gridCodeType].codes;

  // stay on the first item
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id === item.id) {
      exitEditAll(inProgress);

      // start editing for the previous item in the same grid. Stay on the first item
      const focusedItemId = ind;
      const focusedItem = inProgress[item.gridCodeType].codes[focusedItemId];
      if (!focusedItem) {
        break;
      }
      // Store `empty` can lag behind Tab (blur still in-flight); honor typed code from keypress.
      const codeOnRow = (typedCode ?? focusedItem.code ?? item.code ?? "").trim();
      const editField =
        (
          focusedItem.empty &&
          codeOnRow === "" &&
          focusedItem.gridCodeType !== GridCodeType.OUTPROCEDURES
        ) ?
          "code"
        : fieldName;
      setCodeRowInEdit(inProgress, item.gridCodeType, focusedItem.id, editField);
      inProgress.lastActiveCodeId = focusedItem.id;
      break;
    }
  }
}

function focusLastGridRow(
  state: EncounterState,
  _inProgress: EncounterEntity,
  gridType?: GridCodeType,
  payload?: CodeGridKeypress,
) {
  if (!gridType) {
    return;
  }

  const inProgress = _inProgress;

  if (gridType === GridCodeType.ADMIT_DX) {
    // skip AdmitDX Description for empty code
    const currentAdmitDX = (inProgress.admitDiagnosisCode.code || "").trim();

    inProgress.admitDiagnosisCode = {
      ...inProgress.admitDiagnosisCode,
      editField:
        (
          (payload?.fieldSettings &&
            isHiddenField(payload.fieldSettings, "AdmitDiagnosis.Description")) ||
          currentAdmitDX === ""
        ) ?
          "code"
        : "description",
      inEdit: true,
    };

    inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;

    return;
  }

  const codeGroup = inProgress[gridType];
  if (codeGroup && codeGroup.codes.length > 0) {
    const lastCode = codeGroup.codes[codeGroup.codes.length - 1];

    let editField = "description";

    // more accurate definition
    if (payload) {
      const { isShowingPdxAnalysis, isShowingEccAnalysis } = payload;
      const columns = getGridColumns(
        state,
        gridType,
        isShowingPdxAnalysis,
        payload.columnsSettings,
        payload.fieldSettings,
        isShowingEccAnalysis,
      );
      const previousField = getPreviousField(state, inProgress, lastCode, columns, "");
      editField = previousField || "code";
    }

    const field = isOutProceduresRowEmpty(lastCode) || lastCode.empty ? "code" : editField;
    setCodeRowInEdit(inProgress, gridType, lastCode.id, field);
    inProgress.lastActiveCodeId = lastCode.id;
  }
}

export const handleFocusFistGridField = (state: EncounterState): EncounterState => {
  const inProgress = { ...state.inProgress };
  exitEditAll(inProgress);

  setAdmitDxInEdit(inProgress, "code");

  return {
    ...state,
    inProgress,
  };
};

export const handleCodeGridRemove = (
  state: EncounterState,
  payload: CodeGridChange,
): EncounterState => {
  const changedTime = moment.now();

  const inProgress = {
    ...state.inProgress,
  };

  const collection = inProgress[payload.dataItem.gridCodeType].codes;

  const newCollection: BaseCodeRow[] = [];

  let removedRowIndex = -1;
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id !== payload.dataItem.id) {
      newCollection.push(collection[ind]);
    } else {
      removedRowIndex = ind;
    }
  }

  if (removedRowIndex === -1) {
    return state;
  }

  inProgress[payload.dataItem.gridCodeType] = {
    ...inProgress[payload.dataItem.gridCodeType],
    codes: newCollection,
  };

  const newFocusRow = inProgress[payload.dataItem.gridCodeType].codes[removedRowIndex];

  if (payload.removeRow) {
    let newField = payload.field || "code";
    if (isCellLocked(state, inProgress, newFocusRow, newField)) {
      newField = "code";
    }
    handleCurrentRowFocus(inProgress, newFocusRow, newField);
  }

  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: changedTime,
  };
};

export const handleCodeGridMoveToPdx = (
  state: EncounterState,
  payload: CodeGridChange,
): EncounterState => {
  const item = findItem(state.inProgress, payload.dataItem);
  if (!item) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
  };

  const newCollection: BaseCodeRow[] = [];

  // set item as first
  newCollection.push(item);

  // add all other items
  for (let ind = 0, len = inProgress[item.gridCodeType].codes.length; ind < len; ind++) {
    if (inProgress[item.gridCodeType].codes[ind].id !== payload.dataItem.id) {
      newCollection.push(inProgress[item.gridCodeType].codes[ind]);
    }
  }

  inProgress[item.gridCodeType] = {
    ...inProgress[item.gridCodeType],
    codes: newCollection,
  };
  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handlePdxAnalyzeCompleted = (
  state: EncounterState,
  payload: PDxAnalyzeResultEntity,
): EncounterState => {
  let changed = false;
  const codes = state.inProgress.diagnoses.codes.map((code) => {
    const result = payload[code.id];
    if (!result) {
      return code;
    }

    changed = true;
    return {
      ...code,
      drg: result.drg,
      drgDescription: result.drgDescription,
      drgWeight: result.drgWeight,
      reimbursement: result.drgReimbursement,
    };
  });

  if (!changed) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
    diagnoses: {
      ...state.inProgress.diagnoses,
      codes,
    },
  };
  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
  };
};

export const handleEccAnalyzeCompleted = (
  state: EncounterState,
  payload: ElixhauserAnalyzeResult,
): EncounterState => {
  const inProgress = {
    ...state.inProgress,
  };
  const flagsByKey = new Map(
    payload.flags.filter((flag) => flag.flag).map((flag) => [flag.flag!, flag] as const),
  );

  const resultsByViewId = new Map(
    payload.codeResults
      .filter((result) => result.viewId)
      .map((result) => [result.viewId!, result] as const),
  );

  const collection = inProgress.diagnoses.codes;
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    const code = collection[ind];
    const result = resultsByViewId.get(code.id);

    if (!result) {
      code.mortalityIndexTotal = undefined;
      code.readmissionIndexTotal = undefined;
      code.elixhauserCategories = undefined;
      continue;
    }

    code.mortalityIndexTotal = result.mortalityWeightTotal ?? undefined;
    code.readmissionIndexTotal = result.readmissionWeightTotal ?? undefined;
    code.elixhauserCategories = result.flags
      .map((flag) => flagsByKey.get(flag))
      .filter((flag): flag is NonNullable<typeof flag> => Boolean(flag));
  }

  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
  };
};

export const handleCodeGridEnterEditAdmitDX = (state: EncounterState): EncounterState => {
  if (
    state.inProgress.admitDiagnosisCode.inEdit &&
    state.inProgress.admitDiagnosisCode.editField === "code"
  ) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
  };

  exitEditAll(inProgress);

  const admitDiagnosisCode = {
    ...state.inProgress.admitDiagnosisCode,
    editField: "code",
    inEdit: true,
    initialCode: state.inProgress.admitDiagnosisCode.code,
  };

  inProgress.admitDiagnosisCode = admitDiagnosisCode;
  inProgress.lastActiveCodeId = admitDiagnosisCode.id;

  return {
    ...state,
    inProgress,
  };
};

export const handleCodeGridEnterEdit = (
  state: EncounterState,
  payload: CodeGridChange,
): EncounterState => {
  // allow click again on the incorrect field
  if (payload.providers) {
    const allowFutureDates = payload.allowFutureDates ?? false;
    const incorrectField = isIncorrectGridItem(
      payload.dataItem,
      payload.providers,
      allowFutureDates,
    );
    if (
      gridsHaveIncorrectFields(state.inProgress, payload.providers, allowFutureDates) &&
      incorrectField !== payload.field
    ) {
      return state;
    }
  }

  if (payload.dataItem.id === ADMIT_DX_ID) {
    return handleCodeGridEnterEditAdmitDX(state);
  }

  const item = findItem(state.inProgress, payload.dataItem, payload.dataItem.gridCodeType);
  if (!item) {
    return state;
  }
  if (item.inEdit && item.editField === payload.field) {
    return state;
  }

  let inProgress = {
    ...state.inProgress,
  };

  const prevEdited = combineEncounterCodes(state.inProgress).find((i: BaseCodeRow) => i.inEdit);

  if (isOutProceduresRowEmpty(prevEdited) && inProgress.lastActiveCodeId !== item?.id) {
    inProgress = removeInvalidOutProceduresRow(inProgress, prevEdited);
  }

  exitEditAll(inProgress);

  setCodeRowInEdit(
    inProgress,
    payload.gridCodeType || payload.dataItem.gridCodeType,
    payload.dataItem.id,
    payload.field || "",
  );
  inProgress.lastActiveCodeId = payload.dataItem.id;
  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
  };
};

function canBePlacedToGrid(itemType: CodeType, gridType: GridCodeType) {
  switch (itemType) {
    case CodeType.ICD10CM_DX:
    case CodeType.ICD10CM_E:
      return gridType === GridCodeType.DIAGNOSES || gridType === GridCodeType.VISITREASONS;
    case CodeType.ICD10PCS_PR:
      return gridType === GridCodeType.INPROCEDURES;
    case CodeType.CPT4:
    case CodeType.HCPCS:
      return (
        gridType === GridCodeType.OUTPROCEDURES || gridType === GridCodeType.PROFESSIONAL_PROCEDURES
      );
    default:
  }

  throw new Error("Unsupported code type");
}

function nextGridType(
  gridType: GridCodeType,
  fieldSettings: FieldSettings,
  isShowingPdxAnalysis?: boolean,
  facilityPreferences?: FacilityPreferences,
  isInpatient?: boolean,
  userFeatures?: UserFeaturesState,
) {
  const showIcdProcedures =
    isInpatient || (!isInpatient && facilityPreferences?.OutpatientPreferences?.AllowIcdProcedures);
  const isVisitReasonsAvailable =
    !isHiddenField(fieldSettings, "VisitReasons") &&
    !isReadOnlyField(fieldSettings, "VisitReasons");
  const isAdmitDXAvailable =
    !isHiddenField(fieldSettings, "AdmitDiagnosis") &&
    !isReadOnlyField(fieldSettings, "AdmitDiagnosis");

  const isProFeeAllowed = checkProFeeAllowed(
    userFeatures?.packages ?? null,
    facilityPreferences?.GeneralPreferences?.AllowProfessionalCoding,
    facilityPreferences?.AllowProfessionalCodingForCurrentUser,
  );

  switch (gridType) {
    case GridCodeType.ADMIT_DX:
      if (isVisitReasonsAvailable) {
        return GridCodeType.VISITREASONS;
      }
      if (!isShowingPdxAnalysis) {
        return GridCodeType.DIAGNOSES;
      }
      return GridCodeType.INPROCEDURES;

    case GridCodeType.VISITREASONS:
      return !isShowingPdxAnalysis ? GridCodeType.DIAGNOSES : GridCodeType.INPROCEDURES;

    case GridCodeType.DIAGNOSES:
      if (isInpatient) {
        return GridCodeType.INPROCEDURES;
      }
      if (showIcdProcedures) {
        return GridCodeType.INPROCEDURES;
      }
      return GridCodeType.OUTPROCEDURES;

    case GridCodeType.INPROCEDURES:
      return GridCodeType.OUTPROCEDURES;

    case GridCodeType.OUTPROCEDURES:
      if (isProFeeAllowed) {
        return GridCodeType.PROFESSIONAL_PROCEDURES;
      }
      if (isAdmitDXAvailable) {
        return undefined;
      }
      if (isVisitReasonsAvailable) {
        return GridCodeType.VISITREASONS;
      }
      if (!isShowingPdxAnalysis) {
        return GridCodeType.DIAGNOSES;
      }
      return GridCodeType.INPROCEDURES;

    case GridCodeType.PROFESSIONAL_PROCEDURES:
      if (isAdmitDXAvailable) {
        return undefined;
      }
      if (isVisitReasonsAvailable) {
        return GridCodeType.VISITREASONS;
      }
      if (!isShowingPdxAnalysis) {
        return GridCodeType.DIAGNOSES;
      }
      return GridCodeType.INPROCEDURES;

    default:
  }

  return undefined;
}

function prevGridType(
  gridType: GridCodeType,
  isShowingPdxAnalysis?: boolean,
  fieldSettings?: FieldSettings,
  facilityPreferences?: FacilityPreferences,
  isInpatient?: boolean,
) {
  const showIcdProcedures =
    isInpatient || (!isInpatient && facilityPreferences?.OutpatientPreferences?.AllowIcdProcedures);
  const isVisitReasonsAvailable =
    !fieldSettings ||
    (!isHiddenField(fieldSettings, "VisitReasons") &&
      !isReadOnlyField(fieldSettings, "VisitReasons"));
  const isAdmitDXAvailable =
    !fieldSettings ||
    (!isHiddenField(fieldSettings, "AdmitDiagnosis") &&
      !isReadOnlyField(fieldSettings, "AdmitDiagnosis"));

  // skip diagnoses grid in PDX On Mode
  const checkedGridType =
    gridType === GridCodeType.INPROCEDURES && isShowingPdxAnalysis ?
      GridCodeType.DIAGNOSES
    : gridType;
  switch (checkedGridType) {
    case GridCodeType.VISITREASONS:
      return isAdmitDXAvailable ? GridCodeType.ADMIT_DX : GridCodeType.OUTPROCEDURES;
    case GridCodeType.DIAGNOSES: {
      if (isVisitReasonsAvailable) {
        return GridCodeType.VISITREASONS;
      }
      if (isAdmitDXAvailable) {
        return GridCodeType.ADMIT_DX;
      }

      return GridCodeType.OUTPROCEDURES;
    }
    case GridCodeType.INPROCEDURES:
      return GridCodeType.DIAGNOSES;
    case GridCodeType.OUTPROCEDURES:
      if (!showIcdProcedures) {
        return GridCodeType.DIAGNOSES;
      }
      return GridCodeType.INPROCEDURES;
    case GridCodeType.PROFESSIONAL_PROCEDURES:
      return GridCodeType.OUTPROCEDURES;
    default:
  }

  return undefined;
}

function findItem(
  inProgress: EncounterEntity,
  item: BaseCodeRow | string,
  gridCodeType?: GridCodeType,
) {
  if (typeof item === "string" && !gridCodeType) {
    const allCodes = combineEncounterCodes(inProgress);

    const code = allCodes.find((c) => c.id === item);

    return code ?? null;
  }

  const codeGroupId = gridCodeType || typeof item === "string" ? gridCodeType : item.gridCodeType;

  if (!codeGroupId) {
    return null;
  }

  const itemId = typeof item === "string" ? item : item.id;

  const codes = inProgress[codeGroupId]?.codes;

  if (codes) {
    const code = codes.find((c) => c.id === itemId);

    return code ?? null;
  }

  return null;
}

// update item in the clone
function updateItem(
  _inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  itemId: string,
  newItem: BaseCodeRow,
) {
  const inProgress = _inProgress;
  const group = inProgress[gridCodeType];
  const { codes } = group;
  const newCodes: BaseCodeRow[] = [];

  for (let ind = 0, len = codes.length; ind < len; ind++) {
    if (codes[ind].id === itemId) {
      newCodes.push(newItem);
    } else {
      newCodes.push(codes[ind]);
    }
  }

  inProgress[gridCodeType] = { ...group, codes: newCodes };
}

const setCodeRowInEdit = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  itemId: string,
  editField: string,
) => {
  const item = inProgress[gridCodeType].codes.find((code) => code.id === itemId);
  if (!item) {
    return;
  }

  updateItem(inProgress, gridCodeType, itemId, {
    ...item,
    inEdit: true,
    editField,
    initialCode: item.code,
  });
};

const setAdmitDxInEdit = (inProgress: EncounterEntity, editField: string) => {
  inProgress.admitDiagnosisCode = {
    ...inProgress.admitDiagnosisCode,
    inEdit: true,
    editField,
    initialCode: inProgress.admitDiagnosisCode.code,
  };
  inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
};

export const handleCodeGridTryExitEdit = (
  state: EncounterState,
  payload: CodeGridExitEditInfo,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field
  let inProgress = {
    ...state.inProgress,
  };

  const item = state.inProgress.outProcedures.codes.find(
    (code) => code.id === inProgress.lastActiveCodeId,
  );

  // Facility CPT/HCPCS: keep the row only when Code or RC is present.
  // Ancillary-only values (Modifiers/Units/…) must not keep an empty-code row, even if
  // `empty` is still true because the editor save is racing behind this exit.
  if (item && isOutProceduresRowEmpty(item)) {
    inProgress = removeInvalidOutProceduresRow(inProgress, item);
    updateCodes(inProgress);
    return {
      ...state,
      inProgress,
      lastChangeTime: moment.now(),
    };
  }

  if (state.inProgress.admitDiagnosisCode.inEdit || !payload.tabFromLastColumn) {
    exitEditAll(inProgress);
    updateCodes(inProgress);

    return {
      ...state,
      inProgress,
    };
  }

  return state;
};

const focusFirstGridRow = (
  state: EncounterState,
  _inProgress: EncounterEntity,
  nextType?: GridCodeType,
  payload?: CodeGridKeypress,
) => {
  const inProgress = _inProgress;

  // focus Admit_DX
  if (!nextType) {
    setAdmitDxInEdit(inProgress, "code");

    return;
  }

  // focus first item in the next grid
  const nextCodes = inProgress[nextType].codes;
  if (nextCodes.length > 0) {
    let editField = "code";

    // more accurate definition
    if (payload) {
      const { isShowingPdxAnalysis, isShowingEccAnalysis } = payload;
      const columns = getGridColumns(
        state,
        nextType,
        isShowingPdxAnalysis,
        payload.columnsSettings,
        payload.fieldSettings,
        isShowingEccAnalysis,
      );
      const firstField = getNextField(state, inProgress, nextCodes[0], columns, "");
      editField = firstField || "code";
    }

    const field = isOutProceduresRowEmpty(nextCodes[0]) || nextCodes[0].empty ? "code" : editField;
    setCodeRowInEdit(inProgress, nextType, nextCodes[0].id, field);
    inProgress.lastActiveCodeId = nextCodes[0].id;
  }
};

export const handleAdmitDXDescriptionBlur = (
  state: EncounterState,
  value: string,
): EncounterState => ({
  ...state,
  dirty: true,
  inProgress: {
    ...state.inProgress,
    admitDiagnosisCode: {
      ...state.inProgress.admitDiagnosisCode,
      description: value,
      inEdit: undefined,
      editField: undefined,
    },
  },
  lastChangeTime: moment.now(),
});

export const handleNavigateFromAdmitDXDescription = (
  state: EncounterState,
  back: boolean,
  fieldSettings: FieldSettings,
  isShowingPdxAnalysis?: boolean,
): EncounterState => {
  const inProgress = {
    ...state.inProgress,
  };

  exitEditAll(inProgress);

  // do not navigate to grid in pdx analysis mode
  if (isShowingPdxAnalysis || back) {
    inProgress.admitDiagnosisCode = {
      ...state.inProgress.admitDiagnosisCode,
      editField: "code",
      inEdit: true,
      initialCode: state.inProgress.admitDiagnosisCode.code,
    };
    inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
  } else {
    const isVisitReasonsAvailable =
      !isHiddenField(fieldSettings, "VisitReasons") &&
      !isReadOnlyField(fieldSettings, "VisitReasons");
    const firstFocusableIPGrid =
      isShowingPdxAnalysis ? GridCodeType.INPROCEDURES : GridCodeType.DIAGNOSES;
    const firstCodeGridType =
      isVisitReasonsAvailable ? GridCodeType.VISITREASONS : firstFocusableIPGrid;

    const collection = inProgress[firstCodeGridType].codes;
    if (collection.length > 0) {
      setCodeRowInEdit(inProgress, firstCodeGridType, collection[0].id, "code");
      inProgress.lastActiveCodeId = collection[0].id;
    }
  }

  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
  };
};

export const handleCodeGridDragAndDropRow = (
  state: EncounterState,
  payload: CodeGridDragDropRow,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  // not changed position
  const ignoreDropping = payload.toIdx === undefined || payload.fromIdx === payload.toIdx;

  const item = !ignoreDropping ? findItem(state.inProgress, payload.dataItemId) : null;
  // incorrect item or same/incorrect position
  if (!item) {
    // we must execute pending auto resequence now
    if (
      payload.autoResequenceModes &&
      needResequenceEncounterCodes(state.inProgress, payload.autoResequenceModes)
    ) {
      const inProgress = resequenceCodes(state.inProgress, payload.autoResequenceModes);

      return {
        ...state,
        inProgress,
        dirty: true,
        lastChangeTime: moment.now(),
      };
    }

    return state;
  }

  const inProgress = {
    ...state.inProgress,
  };

  const newCollection: BaseCodeRow[] = [];

  // get code types & those codes
  const gridCodes = inProgress[item.gridCodeType].codes;
  const curIdx = gridCodes.findIndex((row) => row.id === payload.dataItemId);
  if (curIdx === -1) {
    return state;
  }

  let ind = 0;
  const len = gridCodes.length;
  while (ind <= len) {
    // we can drop after last row. Skip old item
    if (ind !== curIdx) {
      if (ind < len && payload.fromIdx < payload.toIdx) {
        newCollection.push(gridCodes[ind]);
      }

      if (ind === payload.toIdx) {
        newCollection.push(gridCodes[curIdx]);
      }

      if (ind < len && payload.fromIdx > payload.toIdx) {
        newCollection.push(gridCodes[ind]);
      }
    }

    ind++;
  }

  inProgress[item.gridCodeType] = {
    ...inProgress[item.gridCodeType],
    codes: newCollection,
  };
  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    // mark codes as resequenced to ignore next autoresequence
    codesAutoResequenced: payload.ignoreNextAutoResequence,
    autoResequenceIgnored: payload.ignoreNextAutoResequence || state.autoResequenceIgnored,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleClearGrid = (
  state: EncounterState,
  gridCodeType: GridCodeType,
): EncounterState => {
  const inProgress = {
    ...state.inProgress,
    [gridCodeType]: {
      ...state.inProgress[gridCodeType],
      codes: [],
    },
  };

  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleSetAllPOAToValue = (
  state: EncounterState,
  value: PresentOnAdmission,
): EncounterState => {
  const inProgress = {
    ...state.inProgress,
  };

  const newCollection: BaseCodeRow[] = [];

  const gridCodes = inProgress.diagnoses.codes;
  const len = gridCodes.length;
  const keepExemptPOAValues =
    inProgress.ValidationResult?.Category === EncounterCategory.Inpatient &&
    state.partialFacilityPreferences?.InPatientAutofillPOAForExemptDiagnosisCodes;
  for (let ind = 0; ind < len; ind++) {
    const code = gridCodes[ind];

    const keepExemptPOAValue = keepExemptPOAValues && code.exemptPoa !== undefined;
    const poaValue =
      code.presentOnAdmission || keepExemptPOAValue ? code.presentOnAdmission : value;
    newCollection.push({
      ...code,
      presentOnAdmission: code.empty ? undefined : poaValue,
    });
  }

  inProgress.diagnoses = {
    ...inProgress.diagnoses,
    codes: newCollection,
  };

  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleUpdateExemptPOAValues = (state: EncounterState): EncounterState => {
  const inProgress = {
    ...state.inProgress,
  };

  const newCollection: BaseCodeRow[] = [];

  const gridCodes = inProgress.diagnoses.codes;
  const len = gridCodes.length;
  for (let ind = 0; ind < len; ind++) {
    const code = gridCodes[ind];
    const poaValue = code.exemptPoa;
    let newPOA = code.presentOnAdmission;

    // === undefined -> no need to update
    // === null -> blank
    // === 1 => 1
    if (poaValue !== undefined && !code.empty) {
      newPOA = poaValue === "1" ? PresentOnAdmission.ONE : PresentOnAdmission.EMPTY;
    }

    newCollection.push({
      ...code,
      presentOnAdmission: newPOA,
    });
  }

  inProgress.diagnoses = {
    ...inProgress.diagnoses,
    codes: newCollection,
  };

  updateCodes(inProgress);

  return {
    ...state,
    dirty: true,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleClearFieldInGridAutochanged = (
  state: EncounterState,
  payload: { field: string; gridCodeType: GridCodeType },
): EncounterState => {
  const collection = (state.inProgress as unknown as Partial<Record<GridCodeType, CodeGroup>>)[
    payload.gridCodeType
  ];
  if (!collection) {
    return state;
  }
  let changed = false;
  const newCollection = collection.codes.map((code) => {
    if (!code.autoChanged?.includes(payload.field)) {
      return code;
    }

    changed = true;
    return {
      ...code,
      autoChanged: code.autoChanged.filter((field) => field !== payload.field),
    };
  });

  if (!changed) {
    return state;
  }

  const inProgress = {
    ...state.inProgress,
    [payload.gridCodeType]: {
      ...collection,
      codes: newCollection,
    },
  } as EncounterEntity;

  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
  };
};

export const handleInsertRow = (
  state: EncounterState,
  dataItem: BaseCodeRow,
  insertBefore: boolean,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  const inProgress = {
    ...state.inProgress,
  };

  exitEditAll(inProgress);

  const collection = inProgress[dataItem.gridCodeType].codes;

  const newItem = {
    code: "",
    empty: false,
    gridCodeType: dataItem.gridCodeType,
    id: getId(),
    inEdit: true,
    editField: "code",
    initialCode: "",
    serviceCode: "",
    type: dataItem.type,

    Validations: [],
  };

  inProgress.lastActiveCodeId = newItem.id;

  const newCollection: BaseCodeRow[] = [];
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    if (collection[ind].id === dataItem.id && insertBefore) {
      newCollection.push(newItem);
    }

    // skip any other empty row
    const row = collection[ind];
    const isRemoveRowAsEmpty = shouldRemoveRowAsEmpty(row);
    if (!isRemoveRowAsEmpty) {
      newCollection.push(row);
    }

    if (collection[ind].id === dataItem.id && !insertBefore) {
      newCollection.push(newItem);
    }
  }

  inProgress[dataItem.gridCodeType] = {
    ...inProgress[dataItem.gridCodeType],
    codes: newCollection,
  };

  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleClearEmptyRowsInGrid = (
  state: EncounterState,
  dataItem: BaseCodeRow,
): EncounterState => {
  // LOGIC can ignore this action if we have validation errors and keep focus on the wrong field

  const inProgress = {
    ...state.inProgress,
  };

  exitEditAll(inProgress);

  const collection = inProgress[dataItem.gridCodeType].codes;

  const newCollection: BaseCodeRow[] = [];
  for (let ind = 0, len = collection.length; ind < len; ind++) {
    // skip any empty row, but keep OUTPROCEDURES if revenueCode-only is present
    const row = collection[ind];
    const isRemoveRowAsEmpty = shouldRemoveRowAsEmpty(row);
    if (!isRemoveRowAsEmpty) {
      newCollection.push(row);
    }
  }

  inProgress[dataItem.gridCodeType] = {
    ...inProgress[dataItem.gridCodeType],
    codes: newCollection,
  };

  updateCodes(inProgress);

  return {
    ...state,
    inProgress,
    lastChangeTime: moment.now(),
  };
};

export const handleCodeGridExitAllEdit = (state: EncounterState): EncounterState => {
  const inProgress = { ...state.inProgress };
  exitEditAll(inProgress);
  return {
    ...state,
    inProgress,
  };
};

const CODE_GRID_GROUPS = [
  GridCodeType.DIAGNOSES,
  GridCodeType.INPROCEDURES,
  GridCodeType.OUTPROCEDURES,
  GridCodeType.VISITREASONS,
  GridCodeType.PROFESSIONAL_PROCEDURES,
] as const;

export const exitEditAll = (inprogress: EncounterEntity, exitAdmitDX = true) => {
  for (let g = 0; g < CODE_GRID_GROUPS.length; g++) {
    const gridType = CODE_GRID_GROUPS[g];
    const group = inprogress[gridType];
    const { codes } = group;
    let changed = false;
    const nextCodes: BaseCodeRow[] = new Array(codes.length);

    for (let i = 0; i < codes.length; i++) {
      const code = codes[i];
      if (code.inEdit || code.editField !== undefined) {
        nextCodes[i] = { ...code, inEdit: undefined, editField: undefined };
        changed = true;
      } else {
        nextCodes[i] = code;
      }
    }

    if (changed) {
      inprogress[gridType] = { ...group, codes: nextCodes };
    }
  }

  if (
    exitAdmitDX &&
    (inprogress.admitDiagnosisCode.inEdit || inprogress.admitDiagnosisCode.editField !== undefined)
  ) {
    inprogress.admitDiagnosisCode = {
      ...inprogress.admitDiagnosisCode,
      inEdit: undefined,
      editField: undefined,
    };
  }
};

const autofillProcedureDate = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  dataItem?: BaseCodeRow,
) => {
  if (
    !(gridCodeType === GridCodeType.INPROCEDURES || gridCodeType === GridCodeType.OUTPROCEDURES) ||
    inProgress.ValidationResult?.Category === EncounterCategory.Inpatient
  ) {
    return undefined;
  }

  // Procedure Date is already filled
  if (dataItem?.date) {
    return dataItem.date;
  }

  // admitDate === dischargeDate, admitDate is not empty and admitDate is valid
  if (inProgress.admitDate && inProgress.admitDate === inProgress.dischargeDate) {
    if (!isInvalidFormattedDate(inProgress.admitDate, "Invalid Date")) {
      return inProgress.admitDate;
    }
  }

  return undefined;
};

const findEpisodeCode = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  episode: string,
  skipId: string,
) => {
  // Search in the current grid first
  let firstGrid: BaseCodeRow[] = [];
  if (gridCodeType === GridCodeType.INPROCEDURES) {
    firstGrid = inProgress.inProcedures.codes;
  } else if (gridCodeType === GridCodeType.OUTPROCEDURES) {
    firstGrid = inProgress.outProcedures.codes;
  } else if (gridCodeType === GridCodeType.PROFESSIONAL_PROCEDURES) {
    firstGrid = inProgress.professionalProcedures.codes;
  }

  for (let ind = 0, len = firstGrid.length; ind < len; ind++) {
    const code = firstGrid[ind];
    if (code.episode === episode && code.id !== skipId) {
      return code;
    }
  }

  // Search in other procedure grids
  const otherGrids: BaseCodeRow[][] = [];
  if (gridCodeType !== GridCodeType.INPROCEDURES) {
    otherGrids.push(inProgress.inProcedures.codes);
  }
  if (gridCodeType !== GridCodeType.OUTPROCEDURES) {
    otherGrids.push(inProgress.outProcedures.codes);
  }
  if (gridCodeType !== GridCodeType.PROFESSIONAL_PROCEDURES) {
    otherGrids.push(inProgress.professionalProcedures.codes);
  }

  for (let gridIdx = 0, gridsLen = otherGrids.length; gridIdx < gridsLen; gridIdx++) {
    const otherGrid = otherGrids[gridIdx];
    for (let ind = 0, len = otherGrid.length; ind < len; ind++) {
      const code = otherGrid[ind];
      if (code.episode === episode && code.id !== skipId) {
        return code;
      }
    }
  }

  return null;
};

const findProviderByAttendingMd = (
  attendingMd: string | undefined,
  providers?: IdDescriptionBase[],
): IdDescriptionBase | undefined => {
  if (!attendingMd || !providers?.length) {
    return undefined;
  }

  const attendingMdUpper = attendingMd.toUpperCase();
  return providers.find((item) => {
    const viewId = typeof item.ViewId === "string" ? item.ViewId.toUpperCase() : "";
    const id = typeof item.id === "string" ? item.id.toUpperCase() : "";
    return viewId === attendingMdUpper || id === attendingMdUpper;
  });
};

const canFillProfessionalGridProvider = (
  attendingMd: string | undefined,
  providers?: IdDescriptionBase[],
): boolean => Boolean(findProviderByAttendingMd(attendingMd, providers)?.npi?.trim());

const autofillDefaultProvider = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  facilitySettings?: FacilitySettingsState,
  providers?: IdDescriptionBase[],
) => {
  const shouldAutofillFromOpSetting =
    inProgress.ValidationResult?.Category === EncounterCategory.Outpatient &&
    Boolean(facilitySettings?.preferences.OutpatientPreferences?.AutoFillProviderInChoiceList);

  if (shouldAutofillFromOpSetting) {
    if (gridCodeType === GridCodeType.INPROCEDURES || gridCodeType === GridCodeType.OUTPROCEDURES) {
      return inProgress.attendingMd;
    }

    if (gridCodeType === GridCodeType.PROFESSIONAL_PROCEDURES) {
      return canFillProfessionalGridProvider(inProgress.attendingMd, providers) ?
          inProgress.attendingMd
        : undefined;
    }
  }

  if (
    gridCodeType === GridCodeType.PROFESSIONAL_PROCEDURES &&
    inProgress.domain === DomainType.Practice &&
    inProgress.ValidationResult?.Category === EncounterCategory.Professional &&
    facilitySettings?.preferences.ProfessionalPreferences
      ?.AutofillProviderFieldsToMatchAttendingPhysician
  ) {
    return inProgress.attendingMd;
  }

  return undefined;
};

const autofillDefaultRevenueCode = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  facilitySettings?: FacilitySettingsState,
) => {
  if (gridCodeType !== GridCodeType.OUTPROCEDURES) {
    return undefined;
  }

  if (
    inProgress.ValidationResult?.Category === EncounterCategory.Inpatient ||
    !facilitySettings?.preferences.OutpatientPreferences
  ) {
    return undefined;
  }

  return facilitySettings.preferences.OutpatientPreferences.DefaultRevenueCode;
};

const autofillDefaultPlaceOfService = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  serviceLocations?: IdDescriptionBase[],
) => {
  if (gridCodeType !== GridCodeType.PROFESSIONAL_PROCEDURES) {
    return undefined;
  }

  if (inProgress.domain !== DomainType.Practice) {
    return undefined;
  }

  if (inProgress.placeOfService) {
    return inProgress.placeOfService;
  }

  if (inProgress.serviceLocation && serviceLocations?.length) {
    const selectedServiceLocation = serviceLocations.find(
      (item) =>
        item.ViewId === inProgress.serviceLocation || item.id === inProgress.serviceLocation,
    );
    return selectedServiceLocation?.placeOfService || undefined;
  }

  return undefined;
};

export const isIncorrectGridItem = (
  item: BaseCodeRow,
  providers: IdDescriptionBase[],
  allowFutureDate?: boolean,
) => {
  const allowFutureDates = allowFutureDate ?? false;
  if (item.empty) {
    return false;
  }

  if (
    isDateValidator(item.date as unknown as string, allowFutureDates ? undefined : "Incorrect Date")
  ) {
    return "date";
  }

  // provider is not in the providers list and has too many characters
  if (item.provider && !checkDDLFieldLength(providers, MAX_PROVIDER_LENGTH, item.provider)) {
    return "provider";
  }

  return false;
};

export const gridsHaveIncorrectFields = (
  inProgress: EncounterEntity,
  providers: IdDescriptionBase[],
  allowFutureDate?: boolean,
) => {
  // do not check diagnoses and visitReasons because they have not ui side validation errors

  if (
    inProgress.inProcedures.codes.some((item) =>
      isIncorrectGridItem(item, providers, allowFutureDate),
    )
  ) {
    return true;
  }

  if (
    inProgress.outProcedures.codes.some((item) =>
      isIncorrectGridItem(item, providers, allowFutureDate),
    )
  ) {
    return true;
  }

  if (
    inProgress.professionalProcedures.codes.some((item) =>
      isIncorrectGridItem(item, providers, allowFutureDate),
    )
  ) {
    return true;
  }

  return false;
};

export const gridsHaveIncorrectSpecificFields = (
  inProgress: EncounterEntity,
  providers: IdDescriptionBase[],
  fieldName: string,
  allowFutureDate?: boolean,
) => {
  if (
    inProgress.inProcedures.codes.some(
      (item) => isIncorrectGridItem(item, providers, allowFutureDate) === fieldName,
    )
  ) {
    return true;
  }

  if (
    inProgress.outProcedures.codes.some(
      (item) => isIncorrectGridItem(item, providers, allowFutureDate) === fieldName,
    )
  ) {
    return true;
  }

  if (
    inProgress.professionalProcedures.codes.some(
      (item) => isIncorrectGridItem(item, providers, allowFutureDate) === fieldName,
    )
  ) {
    return true;
  }

  return false;
};
